import { format, formatISO, isBefore, parseISO, startOfDay } from "date-fns";

import { GoalHistory } from "../../../../../api/services";

const PRE_SYMBOLS = ["$"];
const POST_SYMBOLS = ["%"];

const getNumberOfDecimalPlaces = (num: number, isMonetary = false) => {
	let decimalDigits = 0;
	const absoluteValue = Math.abs(num);

	if (isMonetary && absoluteValue < 100) {
		decimalDigits = 2;
	} else if (absoluteValue % 1 === 0) {
		decimalDigits = 0;
	} else if (absoluteValue < 10) {
		decimalDigits = 2;
	} else if (absoluteValue > 10 && absoluteValue < 100) {
		decimalDigits = 1;
	} else {
		decimalDigits = 0;
	}

	return decimalDigits;
};

export const getProgressLabels = (props: {
	value: number;
	units: string;
	truncateSubtextChars?: number;
	decimalPlacesOverride?: number;
}): { mainText?: string; subText?: string } => {
	const { value, units, truncateSubtextChars, decimalPlacesOverride } = props;
	const label = { mainText: "", subText: "" };

	const trimmedUnits = units?.trim() ?? "";
	const preSymbol = PRE_SYMBOLS.find((symbol) => trimmedUnits === symbol) || "";
	const postSymbol = POST_SYMBOLS.find((symbol) => trimmedUnits === symbol) || "";
	const unitsWithoutSymbol = trimmedUnits ? trimmedUnits.replace(preSymbol, "").replace(postSymbol, "") : "";

	let progressValue = "";
	const progress = value || 0;
	const isProgressNegative = progress < 0;
	let absoluteValueProgress = Math.abs(progress);
	const suffixes = ["", "K", "M", "B", "T", "Qa", "Qi", "Sx", "Sp", "Oc", "No", "De"];
	let suffixIndex = 0;

	while (absoluteValueProgress >= 1000) {
		absoluteValueProgress /= 1000;
		suffixIndex++;
	}

	progressValue = absoluteValueProgress.toFixed(
		decimalPlacesOverride
			? decimalPlacesOverride
			: getNumberOfDecimalPlaces(absoluteValueProgress, trimmedUnits === "$")
	);

	let progressAbreviation = suffixes[suffixIndex];
	//sci notation fallback
	if (progressAbreviation === undefined) {
		progressValue = value.toExponential(0);
		progressAbreviation = "";
	}

	label.mainText = `${isProgressNegative ? "-" : ""}${preSymbol}${progressValue}${progressAbreviation}${postSymbol}`;
	label.subText = `${
		truncateSubtextChars && unitsWithoutSymbol.length > truncateSubtextChars
			? unitsWithoutSymbol.substring(0, truncateSubtextChars) + "..."
			: unitsWithoutSymbol
	}`;

	return label;
};

export const getGoalProgressHistoryData = (startDate: Date, historyProgress: GoalHistory[]) => {
	const formattedStartDate = format(startDate, "yyyy-MM-dd");

	const filteredHistory = historyProgress?.reduce((acc, curr) => {
		const date = curr.as_of;
		const dateString = format(date, "yyyy-MM-dd");

		if (!acc[dateString]) {
			acc = { ...acc, [dateString]: curr };
		} else if (parseISO(acc[dateString].as_of) < curr.as_of) {
			acc[dateString] = { ...acc[dateString], ...curr };
		}

		return acc;
	}, []);

	if (historyProgress.length > 0) {
		//is there any entries before the start date?
		const hasEntriesBeforeStartDate = historyProgress.some((entry) => {
			const entryDate = entry.as_of;
			return isBefore(entryDate, startDate);
		});

		// if there are no entries before the start date, add a 0 entry
		if (!hasEntriesBeforeStartDate) {
			filteredHistory[formattedStartDate] = filteredHistory[formattedStartDate] || {
				as_of: startDate,
				change: { value: 0 },
			};
		}
	}

	const formattedData = Object.keys(filteredHistory)
		.map((key) => {
			const beginningOfAsOfDate = startOfDay(filteredHistory[key].as_of);
			return {
				label: formatISO(beginningOfAsOfDate),
				value: filteredHistory[key].change.value,
			};
		})
		.sort((a, b) => {
			const aDate = parseISO(a.label);
			const bDate = parseISO(b.label);

			return aDate < bDate ? -1 : 1;
		});

	return formattedData;
};

export const getProjectedValueToToday = (startDate: Date, endDate: Date, target: number, optionalFirstEntry?: Date) => {
	const todaysDate = startOfDay(new Date());
	const endGraphDate = endDate < todaysDate ? todaysDate : endDate;
	const formattedStartDate = format(startDate, "yyyy-MM-dd");
	const formattedEndGraphDate = format(endGraphDate, "yyyy-MM-dd");
	const formattedTodaysDate = format(todaysDate, "yyyy-MM-dd");

	let projectedData: { label: string; value: number | null }[] = [];
	const numberOfDays = Math.round((endGraphDate.getTime() - startDate.getTime()) / (1000 * 3600 * 24));

	if (endDate < todaysDate) {
		const formattedEndDate = format(endDate, "yyyy-MM-dd");
		projectedData = [
			{
				label: formattedStartDate,
				value: 0,
			},
			{
				label: formattedEndDate,
				value: target,
			},
			{
				label: formattedEndGraphDate,
				value: target,
			},
		];
	} else if (startDate > todaysDate) {
		projectedData = [
			{
				label: formattedTodaysDate,
				value: null,
			},
			{
				label: formattedStartDate,
				value: 0,
			},
			{
				label: formattedEndGraphDate,
				value: target,
			},
		];
	} else {
		const daysLeft = Math.round((endGraphDate.getTime() - todaysDate.getTime()) / (1000 * 3600 * 24));
		const percentLeft = daysLeft / numberOfDays;
		const projectedValue = target * (1 - percentLeft);

		projectedData = [
			{
				label: formattedStartDate,
				value: 0,
			},
			{
				label: formattedTodaysDate,
				value: projectedValue,
			},
			{
				label: formattedEndGraphDate,
				value: target,
			},
		];
	}

	if (optionalFirstEntry) {
		const formattedFirstEntryDate = format(optionalFirstEntry, "yyyy-MM-dd");
		const firstEntry = {
			label: formattedFirstEntryDate,
			value: 0,
		};
		projectedData.unshift(firstEntry);
	}

	return projectedData;
};
